import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";
import { MapPin, ShieldCheck, ArrowRight, Compass, Phone, Mail, Clock, Bike, Car, Truck, Zap } from "lucide-react";

const VEHICLE_ICON = { two_wheeler: Bike, three_wheeler: Zap, four_wheeler: Car, commercial: Truck };

function buildMapEmbed(p) {
  if (p.latitude != null && p.longitude != null) {
    return `https://maps.google.com/maps?q=${p.latitude},${p.longitude}&z=15&output=embed`;
  }
  const q = encodeURIComponent(`${p.store_name}, ${p.address}, ${p.city} ${p.pincode}, ${p.state}, India`);
  return `https://maps.google.com/maps?q=${q}&z=14&output=embed`;
}

function buildDirectionsUrl(p) {
  if (p.latitude != null && p.longitude != null) {
    return `https://www.google.com/maps/dir/?api=1&destination=${p.latitude},${p.longitude}`;
  }
  const q = encodeURIComponent(`${p.store_name}, ${p.address}, ${p.city} ${p.pincode}, ${p.state}, India`);
  return `https://www.google.com/maps/dir/?api=1&destination=${q}`;
}

export default function PartnerDetail() {
  const { partnerId } = useParams();
  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/partners/${partnerId}`).then((r) => setPartner(r.data)).catch(() => toast.error("Centre not found")).finally(() => setLoading(false));
  }, [partnerId]);

  if (loading) return <div className="min-h-screen"><Navbar /><div className="p-8 text-muted-foreground">Loading…</div></div>;
  if (!partner) return <div className="min-h-screen"><Navbar /><div className="p-8 text-muted-foreground">Centre not found.</div></div>;

  const mapEmbed = buildMapEmbed(partner);
  const directionsUrl = buildDirectionsUrl(partner);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />

      <section className="border-b border-border bg-mesh-soft">
        <div className="mx-auto max-w-6xl px-6 py-12 md:py-16">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-primary mb-3 flex items-center gap-2">
            <ShieldCheck className="w-3.5 h-3.5" /> RTO-Certified centre
          </div>
          <h1 data-testid="partner-detail-h1" className="font-display text-4xl md:text-5xl font-black tracking-tighter">{partner.store_name}</h1>
          <div className="mt-4 flex items-start gap-2 text-muted-foreground">
            <MapPin className="w-4 h-4 mt-1 flex-shrink-0" />
            <span data-testid="partner-address" className="text-sm">{partner.address}, {partner.city} {partner.pincode}, {partner.state}</span>
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to={`/book/${partner.partner_id}`} data-testid="partner-book-btn">
              <Button size="lg" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-8 font-semibold shadow-lg shadow-primary/25">
                Book at this centre <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <a href={directionsUrl} target="_blank" rel="noopener noreferrer" data-testid="partner-directions-btn">
              <Button size="lg" variant="outline" className="rounded-full border-border text-foreground hover:bg-secondary h-12 px-8">
                <Compass className="w-4 h-4 mr-2" /> Get directions
              </Button>
            </a>
          </div>
        </div>
      </section>

      <section>
        <div className="mx-auto max-w-6xl px-6 py-12 grid md:grid-cols-3 gap-8">
          {/* Map */}
          <div className="md:col-span-2">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-primary mb-4">01 — Location</div>
            <div className="relative border border-border rounded-2xl overflow-hidden shadow-lg" data-testid="partner-map-wrapper">
              <iframe
                data-testid="partner-map-iframe"
                title={`Map of ${partner.store_name}`}
                src={mapEmbed}
                width="100%"
                height="420"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
              <a href={directionsUrl} target="_blank" rel="noopener noreferrer" className="absolute bottom-4 right-4 bg-white border border-border rounded-full px-4 py-2 text-xs font-semibold text-foreground shadow-lg hover:shadow-xl transition-shadow duration-200 flex items-center gap-2">
                <Compass className="w-3.5 h-3.5 text-primary" /> Open in Maps
              </a>
            </div>
          </div>

          {/* Info */}
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-primary mb-4">02 — Details</div>
            <div className="border border-border rounded-2xl bg-white p-6 space-y-5">
              <div>
                <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-1">Contact</div>
                <div className="text-sm font-medium">{partner.contact_name}</div>
                <div className="text-xs text-muted-foreground flex items-center gap-2 mt-2"><Phone className="w-3.5 h-3.5" /> <a href={`tel:${partner.contact_phone}`} className="hover:text-primary">{partner.contact_phone}</a></div>
                <div className="text-xs text-muted-foreground flex items-center gap-2 mt-1"><Mail className="w-3.5 h-3.5" /> <a href={`mailto:${partner.contact_email}`} className="hover:text-primary">{partner.contact_email}</a></div>
              </div>
              <div className="pt-4 border-t border-border">
                <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-2">RTO licence</div>
                <div className="font-mono text-sm">{partner.licence_number}</div>
                <div className="text-xs text-muted-foreground mt-1">{partner.licence_authority}</div>
                <div className="text-xs text-muted-foreground flex items-center gap-1 mt-1"><Clock className="w-3 h-3" /> Valid till {partner.licence_expiry}</div>
              </div>
              <div className="pt-4 border-t border-border">
                <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-2">Vehicles serviced</div>
                <div className="flex flex-wrap gap-1.5">
                  {(partner.services_offered || []).map((s) => {
                    const Icon = VEHICLE_ICON[s] || Car;
                    return <span key={s} className="text-xs bg-secondary border border-border px-2 py-1 rounded-full flex items-center gap-1"><Icon className="w-3 h-3" />{s.replace("_", " ")}</span>;
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
